'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Mail, Lock, Loader2, LogIn } from 'lucide-react';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import GoogleButton from './GoogleButton';
import DemoCredentials from './DemoCredentials';
import { useAuth } from '@/contexts/AuthContext';

interface LoginFormData {
  email: string;
  password: string;
  remember: boolean;
}

interface LoginFormProps {
  onSwitchToRegister: () => void;
}

const DEMO_CREDENTIALS = [
  { email: 'max.mustermann@partygames.de', password: 'Party2026!' },
];

export default function LoginForm({ onSwitchToRegister }: LoginFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { signIn } = useAuth();
  const router = useRouter();

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    formState: { errors },
  } = useForm<LoginFormData>({
    defaultValues: { remember: false },
  });

  const onSubmit = async (data: LoginFormData) => {
    setLoading(true);
    try {
      await signIn(data.email, data.password);
      toast.success('🎉 Willkommen zurück! Du wirst weitergeleitet...');
      router.push('/games');
    } catch (err: any) {
      const msg = err?.message ?? 'Anmeldung fehlgeschlagen.';
      if (msg.toLowerCase().includes('invalid') || msg.toLowerCase().includes('credentials') || msg.toLowerCase().includes('password')) {
        setError('email', { message: 'E-Mail oder Passwort ist falsch.' });
      } else {
        setError('email', { message: msg });
      }
    } finally {
      setLoading(false);
    }
  };

  const fillCredentials = (email: string, password: string) => {
    setValue('email', email);
    setValue('password', password);
  };

  return (
    <div className="animate-fade-in">
      <div className="mb-6">
        <h2 className="font-extrabold text-2xl mb-1">Willkommen zurück! 👋</h2>
        <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
          Melde dich an und spiel weiter.
        </p>
      </div>

      {/* Google OAuth */}
      <GoogleButton label="Mit Google anmelden" />

      {/* Divider */}
      <div className="flex items-center gap-3 my-5">
        <div className="flex-1 h-px" style={{ background: 'var(--border)' }} />
        <span className="text-xs font-medium" style={{ color: 'var(--muted-foreground)' }}>oder</span>
        <div className="flex-1 h-px" style={{ background: 'var(--border)' }} />
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        {/* Email */}
        <div>
          <label className="block text-sm font-medium mb-1.5" htmlFor="login-email">
            E-Mail-Adresse
          </label>
          <div className="relative">
            <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }} />
            <input
              id="login-email"
              type="email"
              autoComplete="email"
              placeholder="deine@email.de"
              className="w-full pl-9 pr-4 py-3 rounded-xl text-sm outline-none transition-all"
              style={{
                background: 'var(--input)',
                border: `1px solid ${errors.email ? '#EF4444' : 'var(--border)'}`,
                color: 'var(--foreground)',
              }}
              onFocus={(e) => {
                if (!errors.email) e.currentTarget.style.borderColor = 'var(--primary)';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(124,58,237,0.15)';
              }}
              onBlur={(e) => {
                if (!errors.email) e.currentTarget.style.borderColor = 'var(--border)';
                e.currentTarget.style.boxShadow = 'none';
              }}
              {...register('email', {
                required: 'E-Mail-Adresse ist erforderlich.',
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: 'Bitte eine gültige E-Mail-Adresse eingeben.',
                },
              })}
            />
          </div>
          {errors.email && (
            <p className="text-xs mt-1.5 font-medium" style={{ color: '#EF4444' }}>
              {errors.email.message}
            </p>
          )}
        </div>

        {/* Password */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="block text-sm font-medium" htmlFor="login-password">
              Passwort
            </label>
            <button
              type="button"
              className="text-xs hover:opacity-80 transition-opacity"
              style={{ color: 'var(--neon-violet)' }}
            >
              Passwort vergessen?
            </button>
          </div>
          <div className="relative">
            <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }} />
            <input
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="••••••••"
              className="w-full pl-9 pr-10 py-3 rounded-xl text-sm outline-none transition-all"
              style={{
                background: 'var(--input)',
                border: `1px solid ${errors.password ? '#EF4444' : 'var(--border)'}`,
                color: 'var(--foreground)',
              }}
              onFocus={(e) => {
                if (!errors.password) e.currentTarget.style.borderColor = 'var(--primary)';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(124,58,237,0.15)';
              }}
              onBlur={(e) => {
                if (!errors.password) e.currentTarget.style.borderColor = 'var(--border)';
                e.currentTarget.style.boxShadow = 'none';
              }}
              {...register('password', {
                required: 'Passwort ist erforderlich.',
                minLength: { value: 6, message: 'Passwort muss mindestens 6 Zeichen haben.' },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded transition-colors hover:bg-white/5"
              style={{ color: 'var(--muted-foreground)' }}
              aria-label={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
            >
              {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
          </div>
          {errors.password && (
            <p className="text-xs mt-1.5 font-medium" style={{ color: '#EF4444' }}>
              {errors.password.message}
            </p>
          )}
        </div>

        {/* Remember me */}
        <div className="flex items-center gap-2">
          <input
            id="remember"
            type="checkbox"
            className="w-4 h-4 rounded accent-primary"
            {...register('remember')}
          />
          <label htmlFor="remember" className="text-sm cursor-pointer" style={{ color: 'var(--muted-foreground)' }}>
            Angemeldet bleiben
          </label>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={loading}
          className="w-full btn-primary flex items-center justify-center gap-2 py-3 font-bold text-sm rounded-xl disabled:opacity-60 disabled:cursor-not-allowed"
          style={{ minHeight: '46px' }}
        >
          {loading ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <>
              <LogIn size={16} />
              Anmelden
            </>
          )}
        </button>
      </form>

      <p className="text-center text-sm mt-5" style={{ color: 'var(--muted-foreground)' }}>
        Noch kein Konto?{' '}
        <button
          onClick={onSwitchToRegister}
          className="font-semibold hover:opacity-80 transition-opacity"
          style={{ color: 'var(--neon-violet)' }}
        >
          Jetzt registrieren
        </button>
      </p>

      {/* Demo Credentials */}
      <DemoCredentials credentials={DEMO_CREDENTIALS} onFill={fillCredentials} />
    </div>
  );
}